import React, { useState } from 'react';
import ReactDOM from 'react-dom';

const TYPE_META = {
  CHECK_IN: {
    title: 'Logged in',
    dot: 'bg-emerald-500 ring-emerald-200 dark:ring-emerald-800',
    line: 'bg-emerald-400/70',
  },
  BREAK_START: {
    title: 'Break started',
    dot: 'bg-amber-500 ring-amber-200 dark:ring-amber-800',
    line: 'bg-amber-300/80',
  },
  BREAK_END: {
    title: 'Joined back',
    dot: 'bg-sky-500 ring-sky-200 dark:ring-sky-800',
    line: 'bg-sky-300/80',
  },
  RELOGIN: {
    title: 'Relogged in',
    dot: 'bg-teal-500 ring-teal-200 dark:ring-teal-800',
    line: 'bg-teal-300/80',
  },
  CHECK_OUT: {
    title: 'Logged out',
    dot: 'bg-rose-500 ring-rose-200 dark:ring-rose-800',
    line: 'bg-rose-300/70',
  },
  CALL_JOIN: {
    title: 'Joined call',
    dot: 'bg-violet-500 ring-violet-200 dark:ring-violet-800',
    line: 'bg-violet-300/80',
  },
  CALL_LEAVE: {
    title: 'Left call',
    dot: 'bg-indigo-500 ring-indigo-200 dark:ring-indigo-800',
    line: 'bg-indigo-300/80',
  },
};

const shiftDate = (yyyyMmDd, deltaDays) => {
  if (!yyyyMmDd) return yyyyMmDd;
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + deltaDays);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

const DayActivityTimelineModal = ({ timeline, onClose, onChangeDate }) => {
  const [loadingDate, setLoadingDate] = useState(false);
  if (!timeline) return null;

  const entries = timeline.entries || [];
  const headerTitle = timeline.userDisplayName || 'Day timeline';

  const goToDate = async (nextDate) => {
    if (!onChangeDate || !nextDate || loadingDate) return;
    setLoadingDate(true);
    try {
      await onChangeDate(nextDate);
    } finally {
      setLoadingDate(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-3 sm:p-5 bg-black/55 backdrop-blur-[2px]">
      <div
        className="relative w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden rounded-2xl shadow-2xl border border-emerald-200/70 dark:border-emerald-800 bg-white dark:bg-gray-800"
        role="dialog"
        aria-labelledby="day-activity-title"
      >
        <div className="relative overflow-hidden px-5 sm:px-6 pt-5 pb-4 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 text-white flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-white/20 transition-colors z-10"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <p className="text-emerald-50/90 text-xs sm:text-sm font-medium">
            {timeline.dayLabel || 'Day'}
            {timeline.dateLabel ? ` · ${timeline.dateLabel}` : ''}
          </p>
          <h2 id="day-activity-title" className="text-2xl sm:text-3xl font-bold tracking-tight mt-1 pr-8">
            {headerTitle}
          </h2>
          <p className="mt-2 text-sm text-emerald-50/90">
            {timeline.checkInTime ? `In ${timeline.checkInTime}` : 'Not logged in yet'}
            {timeline.checkOutTime ? ` · Out ${timeline.checkOutTime}` : timeline.activeDay ? ' · In progress' : ''}
            {timeline.totalCallMinutes > 0 ? ` · Calls ${timeline.totalCallTime}` : ''}
          </p>
          {onChangeDate && timeline.date && (
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                disabled={loadingDate}
                onClick={() => goToDate(shiftDate(timeline.date, -1))}
                className="px-2.5 py-1 rounded-lg bg-white/15 hover:bg-white/25 text-xs font-semibold disabled:opacity-50"
              >
                ← Prev
              </button>
              <input
                type="date"
                value={timeline.date}
                disabled={loadingDate}
                onChange={(e) => goToDate(e.target.value)}
                className="rounded-lg bg-white/15 border border-white/25 text-white text-xs px-2 py-1 focus:outline-none focus:ring-2 focus:ring-white/40"
              />
              <button
                type="button"
                disabled={loadingDate}
                onClick={() => goToDate(shiftDate(timeline.date, 1))}
                className="px-2.5 py-1 rounded-lg bg-white/15 hover:bg-white/25 text-xs font-semibold disabled:opacity-50"
              >
                Next →
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 bg-gradient-to-b from-emerald-50/40 to-white dark:from-gray-900/40 dark:to-gray-800">
          {entries.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-10">
              No attendance activity for this day yet.
            </p>
          ) : (
            <ol className="relative pl-1">
              {entries.map((entry, index) => {
                const meta = TYPE_META[entry.type] || {
                  title: entry.title || entry.type,
                  dot: 'bg-gray-400 ring-gray-200 dark:ring-gray-700',
                  line: 'bg-gray-300/80 dark:bg-gray-600/80',
                };
                const isLast = index === entries.length - 1;
                return (
                  <li key={entry.eventId || `${entry.type}-${index}`} className="relative flex gap-3 pb-6 last:pb-0">
                    <div className="relative flex flex-col items-center w-5 flex-shrink-0">
                      <span
                        className={`relative z-[1] mt-1 w-3.5 h-3.5 rounded-full ring-4 ${meta.dot}`}
                        aria-hidden
                      />
                      {!isLast && (
                        <span
                          className={`absolute top-4 bottom-[-1.5rem] w-0.5 rounded-full ${meta.line}`}
                          aria-hidden
                        />
                      )}
                    </div>
                    <div className="min-w-0 flex-1 -mt-0.5">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {entry.title || meta.title}
                        </p>
                        <p className="text-xs font-semibold tabular-nums text-gray-500 dark:text-gray-400 flex-shrink-0">
                          {entry.timeLabel || '—'}
                        </p>
                      </div>
                      {entry.subtitle && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{entry.subtitle}</p>
                      )}
                      {(entry.deviceLabel || entry.locationLabel) && (
                        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
                          {entry.deviceLabel && (
                            <span>
                              <span className="font-semibold text-gray-600 dark:text-gray-300">Device</span>
                              {' · '}
                              {entry.deviceLabel}
                            </span>
                          )}
                          {entry.locationLabel && (
                            <span>
                              <span className="font-semibold text-gray-600 dark:text-gray-300">Location</span>
                              {' · '}
                              {entry.locationLabel}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <div className="flex-shrink-0 px-4 sm:px-5 py-3.5 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl text-emerald-800 dark:text-emerald-100 text-sm sm:text-base font-bold py-3 transition-colors shadow-sm bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-900/50 dark:hover:bg-emerald-900/70"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DayActivityTimelineModal;
